import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { StringHelper } from '../../shared/utils/string-helper';
import { IKeyValuePair } from 'src/app/shared/services/rest/base.service';
import { RoutesRecognized, Router } from '@angular/router';
import { Occupancy } from 'src/app/shared/models/occupancy';
import { DateHelper } from 'src/app/shared/utils/date-helper';
import { RoomPublicService } from 'src/app/shared/services/rest/room-public.service';
import { MessageService } from 'src/app/shared/services/message.service';
import { MessageType } from 'src/app/shared/models/message';
import { AppConfigService } from 'src/app/shared/services/app-config.service';
import { forkJoin } from 'rxjs';
import { Room } from 'src/app/shared/models/room.model';
import { IInfoScreenUrlParams } from 'src/app/shared/models/iinfo-screen-url-params';
import { UrlHelper } from 'src/app/shared/utils/url-helper';
import { ISearchUrlParams } from 'src/app/shared/models/isearch-url-params';

@Component({
  selector: 'app-room-info-screen',
  templateUrl: './room-info-screen.component.html',
  styleUrls: ['./room-info-screen.component.scss']
})
export class RoomInfoScreenComponent implements OnInit {

  private static readonly elementHeight = 49;

  /**
   * True if component has been initialized
   *
   * @private
   * @type {boolean}
   * @memberof RoomInfoScreenComponent
   */
  private isInitialized: boolean;

  /**
  * List of all Rooms to display
  *
  * @private
  * @type {Room[]}
  * @memberof RoomInfoScreenComponent
  */
  private rooms: DisplayedRoom[] = [];

  /**
   * Number of rooms to display per page
   *
   * @private
   * @type {number}
   * @memberof RoomInfoScreenComponent
   */
  private elementsPerPage: number;

  private currentPage = 999;

  @ViewChild('header') headerElement: ElementRef;

  @ViewChild('footer') footerElement: ElementRef;

  /**
   * base path to the asset folder.
   *
   * @type {string}
   * @memberof RoomInfoScreenComponent
   */
  assetPath: string;

  /**
   * string displayed as watch
   *
   * @type {string}
   * @memberof RoomInfoScreenComponent
   */
  watch: string;

  /**
   * string displayed as title of the screen
   *
   * @type {string}
   * @memberof RoomInfoScreenComponent
   */
  description: string;

  /**
   * Display the QR-Code module if set to true
   *
   * @type {boolean}
   * @memberof RoomInfoScreenComponent
   */
  displayQrCode: boolean;

  /**
   * Base url of the installed Room reservation
   * Used as base-url for the QR-code in RoomInfoscreen
   *
   * @type {string}
   * @memberof RoomInfoScreenComponent
   */
  qrCodeUrl: string;

  timelineLeft: string;

  timelineHeight: string;

  /**
   * Currently displayed rooms
   *
   * @type {Room[]}
   * @memberof RoomInfoScreenComponent
   */
  displayRooms: DisplayedRoom[] = [];

  /**
   * List if hours to display
   *
   * @type {number[]}
   * @memberof RoomInfoScreenComponent
   */
  hours: number[] = [];

  /**
   * List of time-slices to display bars
   *
   * @type {Date[]}
   * @memberof RoomInfoScreenComponent
   */
  slices: number[] = [];

  /**
   * update timer
   *
   * @private
   * @type {NodeJS.Timer}
   * @memberof RoomInfoScreenComponent
   */
  private timer: NodeJS.Timer;

  private loader: NodeJS.Timer;

  private params: IInfoScreenUrlParams;

  constructor(
    private roomService: RoomPublicService,
    private messages: MessageService,
    private router: Router
  ) {
  }

  ngOnInit() {
    // load params and setup
    this.router.events.subscribe(event => {
      if (!!event && event instanceof RoutesRecognized) {

        this.params = UrlHelper.fetchInfoScreenUrlParams(event.state.root.queryParams);
        if (this.params.filter === null && this.params.idrooms === null) {
          const msg = 'Parameter "filter" or "idrooms" must be set!';
          this.messages.add(msg, MessageType.Error);
          throw new Error(msg);
        }

        this.description = this.params.description;
        this.assetPath = AppConfigService.settings.assetPath;
        this.init();
      }
    });
  }

  /**
   * Initialize the component
   *
   * @private
   * @returns
   * @memberof RoomInfoScreenComponent
   */
  private init() {
    if (this.isInitialized) {
      return;
    }

    this.ensureParams();

    this.isInitialized = true;
    const showHours = this.params.tohour - this.params.fromhour;

    let min = 0;
    for (let num = this.params.fromhour; num <= this.params.fromhour + showHours; min += 5) {
      if (min > 55) {
        this.hours.push(this.getHour(num));
        min = 0;
        num++;
      }
      this.slices.push(DisplayedRoom.getIndex(num, min));
    }

    this.loadData();
    this.updateTime();
    this.initQrCode();
  }

  private initQrCode() {
    this.displayQrCode = !this.params.hideqrcode && StringHelper.hasAny(AppConfigService.settings.qrCodeBaseUrl);

    if (!this.displayQrCode) {
      return;
    }

    const params = ['startSearch=1'];
    if (this.params.filter) {
      params.push(nameof<ISearchUrlParams>('roomNumber') + '=' + encodeURIComponent(this.params.filter));
    }

    if (this.params.idrooms) {
      params.push(nameof<ISearchUrlParams>('idRoom') + '=' + encodeURIComponent(this.params.idrooms));
    }

    this.qrCodeUrl = UrlHelper.trimUrl(AppConfigService.settings.qrCodeBaseUrl) + '/?' + params.join('&');
  }

  private loadData() {
    this.ensureParams();

    // rooms
    const roomFilterArgs = this.createRoomFilterArguments(this.params);
    const occupancyArgs = this.createOccupancyFilterArguments(this.params);

    forkJoin([this.roomService.getRooms(roomFilterArgs), this.roomService.getOccupancies(occupancyArgs)])
      .subscribe(x => {
        const roomDict: { [id: number]: DisplayedRoom; } = {};
        // rooms
        x[0].forEach((r: Room) => {
          const dr = new DisplayedRoom();
          dr.ResourceId = r.Id;
          dr.Ressource = r.Designation;
          roomDict[r.Id] = dr;
        });

        // occupancies
        x[1].map(o => Occupancy.create(o)).forEach((occupancy: Occupancy) => {
          if (occupancy.ResourceId in roomDict) {
            roomDict[occupancy.ResourceId].addOccupancy(occupancy);
          } else {
            const msg = `could not find room "${occupancy.ResourceId}" for occupany "${occupancy.IdObject}"`;
            this.messages.add(msg, MessageType.Waning);
          }
        });

        const newRooms = new Array<DisplayedRoom>();
        for (const key in roomDict) {
          if (roomDict.hasOwnProperty(key)) {
            newRooms.push(roomDict[key]);
          }
        }

        // start update timer on first load
        const startUpdateing = this.rooms.length === 0;

        this.rooms = newRooms.sortByProp(r => r.Ressource);

        if (startUpdateing) {
          this.updateScreen();
        }

        // timeout of 5 min.
        const refreshInterval = 5 * 60000

        // clear scheduled updates
        clearTimeout(this.loader);
        this.loader = setTimeout(() => { this.loadData(); }, refreshInterval);
      });
  }

  /**
   * ensure params are present
   *
   * @private
   * @memberof RoomInfoScreenComponent
   */
  private ensureParams() {
    if (this.params === null) {
      throw new Error('params not set yet!');
    }
  }

  /**
   * Calculate sizes and offsets
   *
   * @private
   * @memberof RoomInfoScreenComponent
   */
  private calculateSizes() {
    const header: HTMLElement = this.headerElement.nativeElement;
    const footer: HTMLElement = this.footerElement.nativeElement;
    const tableHeaderSize = RoomInfoScreenComponent.elementHeight;
    const availableSpace = window.innerHeight - (header.offsetHeight + footer.offsetHeight + tableHeaderSize);
    this.elementsPerPage = Math.floor(availableSpace / RoomInfoScreenComponent.elementHeight);
    this.timelineHeight = (availableSpace + tableHeaderSize) + 'px';
  }

  private getHour(num: number): number {
    if (num > 23) {
      return num - 24;
    }
    return num;
  }

  isCurrentHour(hour: number): boolean {
    return new Date().getHours() === hour;
  }

  isFullHour(index: number): boolean {
    return index % 12 === 0;
  }

  /**
   * update watch and timeline
   *
   * @memberof RoomInfoScreenComponent
   */
  updateTime() {
    const t = new Date();
    const min = t.getMinutes();

    this.watch = t.getHours() + ':' + StringHelper.padLeft(min.toString(), 2);
    this.timelineLeft = (min / 60) * 100 - 1 + '%';
    setTimeout(() => { this.updateTime(); }, 1000);
  }

  /**
   * handle paging and rendering
   *
   * @memberof RoomInfoScreenComponent
   */
  updateScreen() {
    this.calculateSizes();

    if (this.currentPage * this.elementsPerPage >= this.rooms.length) {
      this.currentPage = 1;
    } else {
      this.currentPage++;
    }

    const startIndex = (this.currentPage * this.elementsPerPage) - this.elementsPerPage;
    let endIndex = this.currentPage * this.elementsPerPage;
    if (endIndex > this.rooms.length) {
      endIndex = this.rooms.length;
    }

    this.displayRooms = this.rooms.slice(startIndex, endIndex);

    // clear scheduled updates
    clearTimeout(this.timer);
    this.timer = setTimeout(() => { this.updateScreen(); }, 7000);
  }

  /**
   * returns current or debug date
   *
   * @private
   * @returns {Date}
   * @memberof RoomInfoScreenComponent
   */
  private getDate(): Date {
    return new Date();
  }

  private createRoomFilterArguments(params: IInfoScreenUrlParams): Array<IKeyValuePair<string, string>> {

    const parameter = new Array<IKeyValuePair<string, string>>();

    if (StringHelper.hasAny(params.filter)) {
      parameter.push({
        key: 'filter.' + nameof<Room>('Designation'),
        value: params.filter
      });
    }

    if (StringHelper.hasAny(params.idrooms)) {
      parameter.push({
        key: 'filter.' + nameof<Room>('Id'),
        value: params.idrooms
      });
    }

    return parameter;
  }

  private createOccupancyFilterArguments(params: IInfoScreenUrlParams): Array<IKeyValuePair<string, string>> {

    const parameter = new Array<IKeyValuePair<string, string>>();
    const now = this.getDate();

    parameter.push({
      key: 'filter.' + nameof<Occupancy>('DateTimeFrom'),
      value: String(DateHelper.getUnixTime(new Date(now.getFullYear(), now.getMonth(), now.getDate())))
    });

    parameter.push({
      key: 'filter.' + nameof<Occupancy>('DateTimeTo'),
      value: String(DateHelper.getUnixTime(new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999)))
    });

    if (StringHelper.hasAny(params.filter)) {
      parameter.push({
        key: 'filter.' + nameof<Occupancy>('Ressource'),
        value: params.filter
      });
    }

    if (StringHelper.hasAny(params.idrooms)) {
      parameter.push({
        key: 'filter.' + nameof<Occupancy>('ResourceId'),
        value: params.idrooms
      });
    }

    return parameter;
  }

}

/**
 * Display model for room-info-screen
 *
 * @class DisplayedRoom
 */
class DisplayedRoom {

  /**
   * IdRessource
   *
   * @type {number}
   * @memberof DisplayedRoom
   */
  ResourceId: number;

  /**
   * RessourceBez
   *
   * @type {string}
   * @memberof DisplayedRoom
   */
  Ressource: string;

  /**
   * Occupied time
   *
   * @private
   * @type {Array<boolean>}
   * @memberof DisplayedRoom
   */
  private Minutes: Array<boolean> = [];

  public constructor() {
    this.Minutes = new Array(288);
  }

  /**
   * Create numeric representation from time
   *
   * @private
   * @static
   * @param {Date} date
   * @returns {number}
   * @memberof DisplayedRoom
   */
  private static getIndexDate(date: Date): number {
    return DisplayedRoom.getIndex(date.getHours(), date.getMinutes());
  }

  /**
   * Create numeric representation from time
   *
   * @static
   * @param {number} hours
   * @param {number} minutes
   * @returns {number}
   * @memberof DisplayedRoom
   */
  public static getIndex(hours: number, minutes: number): number {
    return hours * 12 + Math.ceil(minutes / 5);
  }

  /**
   * Add an occupany to the model
   *
   * @param {Occupancy} occupancy
   * @memberof DisplayedRoom
   */
  public addOccupancy(occupancy: Occupancy) {
    const start = DisplayedRoom.getIndexDate(occupancy.DateTimeFrom);
    const end = DisplayedRoom.getIndexDate(occupancy.DateTimeTo);
    for (let i = start; i < end; i++) {
      this.Minutes[i] = true;
    }
  }

  /**
   * True if the given time (as numeric representation) is occupied by this room
   *
   * @param {number} index
   * @returns {boolean}
   * @memberof DisplayedRoom
   */
  public isInUse(index: number): boolean {
    return this.Minutes[index] === true;
  }

}
